import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from "@angular/core";
import { LocalComponentStore, METERS_IN_KILOMETER } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import turfCenter from "@turf/center";
import { feature } from "@turf/helpers";
import {
    Cartesian2,
    Cartesian3,
    Color,
    ConstantProperty,
    CustomDataSource,
    DistanceDisplayCondition,
    Entity,
    EntityCollection,
    ImageMaterialProperty,
    LabelGraphics,
    Material,
    MaterialProperty,
    PolygonGraphics,
    PolygonHierarchy,
    PolylineDashMaterialProperty,
    PolylineGraphics,
    PositionProperty,
    Property,
} from "cesium";
import equal from "fast-deep-equal";
import { combineLatestWith, pairwise, startWith, tap } from "rxjs";
import {
    AirspaceElement,
    CesiumMaterial,
    CesiumSupportedMaterialTypes,
    CustomZoneStyle,
    GeoZoneType,
    GeoZonesStyle,
    InfoZoneType,
    SimpleZone,
    SimpleZoneType,
} from "../../../../geo-zones/models/geo-zones.models";
import { MapUtils } from "../../../index";
import { CesiumMapService } from "../../../services/cesium-map.service";
import { createCesiumDashPattern } from "../../../utils/create-cesium-dash-pattern";
import {
    CUSTOM_OPACITY_SETTINGS_FOR_TYPE,
    GEO_ZONE_STYLES,
    LOCAL_INFO_ICON_SETTINGS,
    LocalInfoIcon,
    LocalInfoIconSettings,
    SIMPLE_ZONE_STYLES,
} from "./geographical-zones-layer.data";

interface GeographicalZonesLayerComponentState {
    zones: AirspaceElement[];
    simpleZones: SimpleZone[];
    shouldShow: boolean;
    selectedZoneId: string | undefined;
    globalZoneOpacity: number;
    customZoneStyles: Record<string, CustomZoneStyle> | undefined;
}

interface ZoneData {
    designator?: string;
    borderColor?: string;
    fillColor?: string;
    fillImageScale?: Cartesian2;
    fillOpacity?: number;
    isActive?: boolean;
    isStatic?: boolean;
    isSelected?: boolean;
    isOrdered?: boolean;
    positions: Cartesian3[];
    iconPosition: Cartesian3;
    toTime?: Date;
    borderWidth: number;
    isLocal?: boolean;
    localInformationType?: InfoZoneType;
    simpleZoneType?: SimpleZoneType;
}

interface ZoneCollection {
    data: ZoneData;
    fillEntity: Entity;
    outlineEntity: Entity;
    zoneIconEntity?: Entity;
    zoneAccentIconEntity?: Entity;
}

declare module "cesium" {
    interface Entity {
        designator?: string;
        simpleZone?: {
            id: string;
            type: SimpleZoneType;
        };
    }
}

const DEFAULT_OUTLINE_DASH_LENGTH = 15;
const DEFAULT_OUTLINE_DASH_PATTERN = createCesiumDashPattern("-----------");
const STRIPE_IMAGE = "assets/images/stripe.png";
const DEFAULT_ZONE_BORDER_WIDTH = 2;
const SELECTED_ZONE_BORDER_WIDTH = 4;
const SELECTED_ZONE_FILL_OPACITY = 0.5;
const CLICKABLE_TRANSPARENT_FILL_OPACITY = 0.01;
// eslint-disable-next-line no-magic-numbers
const ZONE_ACCENT_ICON_OFFSET = new Cartesian2(-4, 3);
const ICON_FONT_SIZE = "14px IconFont";
const ZONE_Z_INDEX = 1;
const ZONE_OUTLINE_Z_INDEX = ZONE_Z_INDEX + 1;
const MAX_LOCAL_ZONES_ICON_VISIBLE_DISTANCE_IN_KILOMETERS = 35;
const LOCAL_ZONES_ICON_DISTANCE_CONDITION = new DistanceDisplayCondition(
    0,
    MAX_LOCAL_ZONES_ICON_VISIBLE_DISTANCE_IN_KILOMETERS * METERS_IN_KILOMETER
);

@UntilDestroy()
@Component({
    selector: "dtm-map-geographical-zones-layer",
    template: "",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class GeographicalZonesLayerComponent implements OnDestroy {
    @Input() public set zones(value: AirspaceElement[] | undefined) {
        this.localStore.patchState({ zones: value ?? [] });
    }

    @Input() public set simpleZones(value: SimpleZone[] | undefined) {
        this.localStore.patchState({ simpleZones: value ?? [] });
    }

    @Input() public set shouldShow(value: BooleanInput) {
        this.updateEntitiesVisibility(coerceBooleanProperty(value));
    }

    @Input() public set selectedZoneId(value: string | undefined) {
        this.localStore.patchState({ selectedZoneId: value });
        this.updateEntities(value);
    }

    @Input() public set globalZoneOpacity(value: number) {
        this.localStore.patchState({ globalZoneOpacity: value });
        this.updateEntitiesOpacity(value);
    }

    @Input() public set customZoneStyles(value: Record<string, CustomZoneStyle> | undefined) {
        this.localStore.patchState({ customZoneStyles: value });
    }

    private cesiumService = inject(CesiumMapService);

    protected readonly entitiesCollection = new Map<string, ZoneCollection>();
    private dataSource: CustomDataSource | undefined;

    constructor(private readonly localStore: LocalComponentStore<GeographicalZonesLayerComponentState>) {
        localStore.setState({
            zones: [],
            simpleZones: [],
            shouldShow: false,
            selectedZoneId: undefined,
            globalZoneOpacity: 1,
            customZoneStyles: undefined,
        });
        this.watchZoneEntitiesData();
    }

    public ngOnDestroy(): void {
        if (this.dataSource) {
            this.cesiumService.getViewer().dataSources?.remove(this.dataSource);
        }
    }

    protected getZoneBorderMaterial(zoneData: ZoneData) {
        if (!zoneData.borderColor) {
            return Color.WHITE;
        }
        if (zoneData.isStatic) {
            return Color.fromCssColorString(zoneData.borderColor);
        }

        return new PolylineDashMaterialProperty({
            color: Color.fromCssColorString(zoneData.borderColor),
            dashLength: DEFAULT_OUTLINE_DASH_LENGTH,
            dashPattern: DEFAULT_OUTLINE_DASH_PATTERN,
        });
    }

    protected getZoneFillMaterial(zoneData: ZoneData, opacity: number = 1) {
        if (!zoneData.fillColor) {
            return Color.WHITE.withAlpha(CLICKABLE_TRANSPARENT_FILL_OPACITY);
        }
        if (zoneData.isSelected) {
            return Color.fromCssColorString(zoneData.fillColor).withAlpha(SELECTED_ZONE_FILL_OPACITY);
        }

        if (zoneData.isActive) {
            return Color.fromCssColorString(zoneData.fillColor).withAlpha((zoneData.fillOpacity ?? 1) * opacity);
        }

        if (zoneData.isOrdered) {
            return new ImageMaterialProperty({
                image: STRIPE_IMAGE,
                transparent: true,
                color: Color.fromCssColorString(zoneData.fillColor).withAlpha((zoneData.fillOpacity ?? 1) * opacity),
                repeat: zoneData.fillImageScale,
            });
        }

        return Color.fromCssColorString(zoneData.fillColor).withAlpha(CLICKABLE_TRANSPARENT_FILL_OPACITY);
    }

    private getGeoZoneEntityStyles({ type, geoZoneType }: AirspaceElement): GeoZonesStyle {
        const customOpacity = Object.entries(CUSTOM_OPACITY_SETTINGS_FOR_TYPE).find(([, types]) => types.includes(type))?.[0];
        const elementStyle = GEO_ZONE_STYLES[geoZoneType] ?? GEO_ZONE_STYLES.Default;

        if (customOpacity !== undefined) {
            return {
                ...elementStyle,
                fillOpacity: +customOpacity,
            };
        }

        return elementStyle;
    }

    private getSimpleZoneEntityStyles(type: SimpleZoneType) {
        return SIMPLE_ZONE_STYLES[type] ?? SIMPLE_ZONE_STYLES.Default;
    }

    private watchZoneEntitiesData() {
        this.dataSource = new CustomDataSource();
        this.cesiumService.getViewer().dataSources.add(this.dataSource);

        this.localStore
            .selectByKey("zones")
            .pipe(
                startWith(null),
                pairwise(),
                combineLatestWith(this.localStore.selectByKey("customZoneStyles")),
                tap(([[previous, current], customZoneStyles]) => {
                    this.createOrUpdateEntities(previous, current, customZoneStyles);
                    this.deleteEntities(previous, current);
                }),
                tap(() => this.cesiumService.getScene().requestRender())
            )
            .pipe(untilDestroyed(this))
            .subscribe();

        this.localStore
            .selectByKey("simpleZones")
            .pipe(
                startWith(null),
                pairwise(),
                tap(([previous, current]) => {
                    this.createOrUpdateSimpleZonesEntities(previous, current);
                    this.deleteEntities(previous, current);
                }),
                tap(() => this.cesiumService.getScene().requestRender())
            )
            .pipe(untilDestroyed(this))
            .subscribe();
    }

    private createOrUpdateEntities(
        previous: AirspaceElement[] | null,
        current: AirspaceElement[] | null,
        customZoneStyles?: Record<string, CustomZoneStyle>
    ) {
        return current?.map((item) => {
            const previousItem = previous?.find(({ id }) => id === item.id);

            if (previous && !previousItem?.isSelected && equal(previousItem, item)) {
                return;
            }

            const isSelected = item.id === this.localStore.selectSnapshotByKey("selectedZoneId");
            const customStyle = item.designator ? customZoneStyles?.[item.designator] : undefined;
            const { borderColor, fillColor, fillOpacity } = this.getGeoZoneEntityStyles(item);

            const fillImageScale = MapUtils.getCesiumImageScaleForGeometry(item.geometry);
            const customFillMaterial = customStyle?.fillMaterial
                ? this.prepareMaterialProperty(customStyle.fillMaterial, fillImageScale)
                : undefined;

            const customOutlineMaterial = customStyle?.outlineMaterial
                ? this.prepareMaterialProperty(customStyle.outlineMaterial)
                : undefined;

            const center = turfCenter(item.geometry);
            const zoneData: ZoneData = {
                designator: item.designator,
                borderColor,
                fillColor,
                fillImageScale,
                fillOpacity,
                isActive: item.isActive,
                isStatic: item.isStatic,
                isOrdered: item.isOrdered,
                positions: Cartesian3.fromDegreesArray(item.geometry.coordinates.flat(2)),
                iconPosition: Cartesian3.fromDegrees(center.geometry.coordinates[0], center.geometry.coordinates[1]),
                borderWidth: isSelected ? SELECTED_ZONE_BORDER_WIDTH : DEFAULT_ZONE_BORDER_WIDTH,
                isSelected,
                isLocal: item.geoZoneType === GeoZoneType.Local,
                localInformationType: item.zoneAttributes?.localInformationType,
            };
            const opacity = this.localStore.selectSnapshotByKey("globalZoneOpacity");

            const entities = this.dataSource?.entities;

            if (!entities) {
                return;
            }

            const zoneFillEntity = entities.getOrCreateEntity(item.id);
            zoneFillEntity.polygon = {
                hierarchy: new PolygonHierarchy(zoneData.positions),
                material: customFillMaterial ?? this.getZoneFillMaterial(zoneData, opacity),
                outline: true,
                outlineColor: customOutlineMaterial ?? this.getZoneBorderMaterial(zoneData),
                outlineWidth: zoneData.borderWidth,
                zIndex: ZONE_Z_INDEX,
            } as unknown as PolygonGraphics;

            zoneFillEntity.designator = item.designator;

            const zoneOutlineEntity = entities.getOrCreateEntity(`${item.id}-zone-outline`);
            zoneOutlineEntity.polyline = {
                positions: zoneData.positions,
                material: customOutlineMaterial ?? this.getZoneBorderMaterial(zoneData),
                width: zoneData.borderWidth,
                clampToGround: true,
                zIndex: ZONE_OUTLINE_Z_INDEX,
            } as unknown as PolylineGraphics;

            const zoneCollection: ZoneCollection = {
                data: zoneData,
                fillEntity: zoneFillEntity,
                outlineEntity: zoneOutlineEntity,
            };
            const iconsData = item.geoZoneType !== GeoZoneType.Local ? undefined : this.addIconEntities(item.id, entities, zoneData);
            zoneCollection.zoneIconEntity = iconsData?.zoneIconEntity;
            zoneCollection.zoneAccentIconEntity = iconsData?.zoneAccentIconEntity;

            this.entitiesCollection.set(item.id, zoneCollection);
        });
    }

    private createOrUpdateSimpleZonesEntities(previous: SimpleZone[] | null, current: SimpleZone[] | null) {
        return current?.map((item) => {
            const previousItem = previous?.find(({ id }) => id === item.id);

            if (previous && equal(previousItem, item)) {
                return;
            }

            const isSelected = item.id === this.localStore.selectSnapshotByKey("selectedZoneId");
            const { borderColor, borderWidth, fillColor, fillOpacity } = this.getSimpleZoneEntityStyles(item.type);

            const fillImageScale = MapUtils.getCesiumImageScaleForGeometry(item.geometry);

            const center = item.iconPoint ? feature(item.iconPoint) : turfCenter(item.geometry);
            const zoneData: ZoneData = {
                designator: item.id,
                borderColor,
                fillColor,
                fillImageScale,
                fillOpacity,
                isActive: true,
                isStatic: true,
                isOrdered: false,
                positions: Cartesian3.fromDegreesArray(item.geometry.coordinates.flat(2)),
                iconPosition: Cartesian3.fromDegrees(center.geometry.coordinates[0], center.geometry.coordinates[1]),
                borderWidth: isSelected ? SELECTED_ZONE_BORDER_WIDTH : borderWidth,
                isSelected,
                isLocal: true,
                localInformationType: undefined,
                simpleZoneType: item.type,
            };
            const opacity = this.localStore.selectSnapshotByKey("globalZoneOpacity");

            const entities = this.dataSource?.entities;

            if (!entities) {
                return;
            }

            const zoneFillEntity = entities.getOrCreateEntity(item.id);
            zoneFillEntity.polygon = {
                hierarchy: new PolygonHierarchy(zoneData.positions),
                material: this.getZoneFillMaterial(zoneData, opacity),
                outline: true,
                outlineColor: this.getZoneBorderMaterial(zoneData),
                outlineWidth: zoneData.borderWidth,
                zIndex: ZONE_Z_INDEX,
            } as unknown as PolygonGraphics;

            const simpleZoneEntity = {
                id: item.id,
                type: item.type,
            };

            zoneFillEntity.simpleZone = simpleZoneEntity;

            const zoneOutlineEntity = entities.getOrCreateEntity(`${item.id}-zone-outline`);
            zoneOutlineEntity.polyline = {
                positions: zoneData.positions,
                material: this.getZoneBorderMaterial(zoneData),
                width: zoneData.borderWidth,
                clampToGround: true,
                zIndex: ZONE_OUTLINE_Z_INDEX,
            } as unknown as PolylineGraphics;

            const zoneCollection: ZoneCollection = {
                data: zoneData,
                fillEntity: zoneFillEntity,
                outlineEntity: zoneOutlineEntity,
            };
            const iconsData = this.addIconEntities(item.id, entities, zoneData);
            iconsData.zoneIconEntity.simpleZone = simpleZoneEntity;
            if (iconsData.zoneAccentIconEntity) {
                iconsData.zoneAccentIconEntity.simpleZone = simpleZoneEntity;
            }

            zoneCollection.zoneIconEntity = iconsData.zoneIconEntity;
            zoneCollection.zoneAccentIconEntity = iconsData.zoneAccentIconEntity;

            this.entitiesCollection.set(item.id, zoneCollection);
        });
    }

    private prepareMaterialProperty(
        options: CesiumMaterial,
        imageScale?: Cartesian2,
        isSelected = false
    ): Material | Color | PolylineDashMaterialProperty | ImageMaterialProperty | undefined {
        if (isSelected) {
            return Color.fromCssColorString(options.color).withAlpha(SELECTED_ZONE_FILL_OPACITY);
        }
        switch (options.type) {
            case CesiumSupportedMaterialTypes.Color:
                return Color.fromCssColorString(options.color).withAlpha(options.opacity ?? 1);
            case CesiumSupportedMaterialTypes.Image: {
                const repeat = options.repeat ?? imageScale?.clone();
                if (repeat && options.scale) {
                    Cartesian2.multiplyByScalar(repeat, 1 / options.scale, repeat);
                }

                return new ImageMaterialProperty({
                    image: options.image,
                    transparent: options.isTransparent,
                    color: Color.fromCssColorString(options.color).withAlpha(options.opacity ?? 1),
                    repeat,
                });
            }
            case CesiumSupportedMaterialTypes.PolylineDash:
                return new PolylineDashMaterialProperty({
                    color: Color.fromCssColorString(options.color),
                    dashLength: options.dashLength ?? DEFAULT_OUTLINE_DASH_LENGTH,
                    dashPattern: options.dashPattern ?? DEFAULT_OUTLINE_DASH_PATTERN,
                });
            default:
                return undefined;
        }
    }

    private addIconEntities(zoneId: string, entities: EntityCollection, zoneData: ZoneData) {
        const localInfoIconSettings = this.getLocalInfoIconSettings(zoneData);
        const mainIconSettings = this.getIconSettings(zoneData, 0);
        const iconBackgroundColor = this.getIconBackgroundColor(localInfoIconSettings);
        const iconBackgroundPadding = this.getIconBackgroundPadding(localInfoIconSettings);
        const distanceDisplayCondition =
            zoneData.simpleZoneType === SimpleZoneType.DronePoint ? undefined : LOCAL_ZONES_ICON_DISTANCE_CONDITION;
        const zoneIconEntity = entities.getOrCreateEntity(`${zoneId}-zone-icon`);
        zoneIconEntity.position = zoneData.iconPosition as unknown as PositionProperty;
        zoneIconEntity.label = {
            text: this.getIconCode(mainIconSettings),
            scale: 2,
            font: ICON_FONT_SIZE,
            fillColor: this.getIconColor(mainIconSettings),
            ...(iconBackgroundColor
                ? {
                      showBackground: true,
                      backgroundColor: iconBackgroundColor,
                      backgroundPadding: iconBackgroundPadding,
                  }
                : {}),
            outlineColor: Color.WHITE,
            distanceDisplayCondition,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
        } as unknown as LabelGraphics;

        let zoneAccentIconEntity;

        const accentIconSettings = this.getIconSettings(zoneData, 1);
        if (accentIconSettings) {
            zoneAccentIconEntity = entities.getOrCreateEntity(`${zoneId}-zone-accent-icon`);
            zoneAccentIconEntity.position = zoneData.iconPosition as unknown as PositionProperty;
            zoneAccentIconEntity.label = {
                position: zoneData.iconPosition,
                text: this.getIconCode(accentIconSettings),
                scale: 2,
                font: ICON_FONT_SIZE,
                fillColor: this.getIconColor(accentIconSettings),
                outlineColor: Color.WHITE,
                pixelOffset: ZONE_ACCENT_ICON_OFFSET,
                distanceDisplayCondition,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            } as unknown as LabelGraphics;
        }

        return { zoneIconEntity, zoneAccentIconEntity };
    }

    private getIconSettings(zone: ZoneData, index: number) {
        return this.getLocalInfoIconSettings(zone)?.icon[index] ?? undefined;
    }

    private getLocalInfoIconSettings(zone: ZoneData): LocalInfoIconSettings | undefined {
        if (zone.isLocal && zone.localInformationType) {
            return LOCAL_INFO_ICON_SETTINGS[zone.localInformationType];
        }

        if (zone.simpleZoneType) {
            return LOCAL_INFO_ICON_SETTINGS[zone.simpleZoneType];
        }

        return undefined;
    }

    private getIconCode(iconSetting: LocalInfoIcon | undefined) {
        if (iconSetting) {
            return String.fromCharCode(parseInt(iconSetting.code, 16));
        }

        return undefined;
    }

    private getIconColor(iconSetting: LocalInfoIcon | undefined) {
        if (iconSetting) {
            return Color.fromCssColorString(iconSetting.color);
        }

        return undefined;
    }

    private getIconBackgroundColor(iconSettings: LocalInfoIconSettings | undefined) {
        if (iconSettings?.background) {
            return Color.fromCssColorString(iconSettings.background.color);
        }

        return undefined;
    }

    private getIconBackgroundPadding(iconSettings: LocalInfoIconSettings | undefined) {
        if (iconSettings?.background?.padding) {
            return new Cartesian2(iconSettings.background.padding, iconSettings.background.padding);
        }

        return new Cartesian2(0, 0);
    }

    private deleteEntities(previousElements: { id: string }[] | null, newElements: { id: string }[] | null) {
        previousElements
            ?.filter((previousItem) => !newElements?.some(({ id }) => previousItem.id === id))
            .forEach(({ id }) => {
                const entityData = this.entitiesCollection.get(id);

                if (!entityData) {
                    return;
                }

                const { data, ...entities } = entityData;

                Object.values(entities).forEach((entity) => this.dataSource?.entities.remove(entity));
                this.entitiesCollection.delete(id);
            });
    }

    private updateEntities(id: string | undefined) {
        const opacity = this.localStore.selectSnapshotByKey("globalZoneOpacity");
        const customZoneStyles = this.localStore.selectSnapshotByKey("customZoneStyles");
        this.entitiesCollection.forEach(({ data, fillEntity, outlineEntity }, key) => {
            const isSelected = key === id;
            const shouldUpdate = isSelected || data.isSelected;
            if (!shouldUpdate) {
                return;
            }

            data.isSelected = isSelected;
            data.borderWidth = isSelected ? SELECTED_ZONE_BORDER_WIDTH : DEFAULT_ZONE_BORDER_WIDTH;
            const customZoneStyle = data.designator ? customZoneStyles?.[data.designator] : undefined;
            const customFillMaterial = customZoneStyle?.fillMaterial
                ? this.prepareMaterialProperty(customZoneStyle?.fillMaterial, data.fillImageScale, isSelected)
                : undefined;
            if (fillEntity.polygon) {
                fillEntity.polygon.material = (customFillMaterial ??
                    this.getZoneFillMaterial(data, opacity)) as unknown as MaterialProperty;
                fillEntity.polygon.zIndex = (isSelected ? ZONE_OUTLINE_Z_INDEX + 1 : ZONE_Z_INDEX) as unknown as ConstantProperty;
            }
            if (outlineEntity.polyline) {
                outlineEntity.polyline.width = data.borderWidth as unknown as Property;
                outlineEntity.polyline.zIndex = (isSelected
                    ? ZONE_OUTLINE_Z_INDEX + 2
                    : ZONE_OUTLINE_Z_INDEX) as unknown as ConstantProperty;
            }
        });

        this.cesiumService.getScene().requestRender();
    }

    private updateEntitiesOpacity(opacity: number = 1) {
        this.entitiesCollection.forEach(({ data, fillEntity }) => {
            if (!fillEntity.polygon) {
                return;
            }

            fillEntity.polygon.material = this.getZoneFillMaterial(data, opacity) as unknown as MaterialProperty;
        });
        this.cesiumService.getScene().requestRender();
    }

    private updateEntitiesVisibility(shouldShow: boolean) {
        if (!this.dataSource) {
            return;
        }
        this.dataSource.show = shouldShow;
        this.cesiumService.getScene().requestRender();
    }
}
